import React, { useMemo } from 'react';
import { ConfirmModal } from '../../../commons/components/ConfirmModal';
import { openUrlInNewTab } from '../../../util/window-utils';
import useAnalytics from '../../../web-analytics/webAnalyticsContext';
import { billingPortalConfig } from '../../../commons/config';
import { AnalyticsEvent } from '../../../web-analytics/AnalyticsEvent';
import { useTimelineData } from '../../hooks/useTimelineData';
import _ from 'lodash';
import { SpecialBenefit } from '../Timeline';
import { InvoiceAndBenefits } from '../../types';
import useAnalyticsPage from '../../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../../web-analytics/Page';
import { StyleVariables } from '../../../commons/styleConstants';
import { GiftBox } from '../../../assets/icons';

const isInvoicePresent = (invoiceAndBenefits: InvoiceAndBenefits) =>
  (typeof invoiceAndBenefits.invoice === 'object' && invoiceAndBenefits.invoice) ||
  invoiceAndBenefits.referenceInvoice;

export const BenefitsStep = (props: { onNext: () => void; onCancel: () => void }) => {
  useAnalyticsPage(Page.CANCELLATION_WIZARD_BENEFITS);

  const { onNext, onCancel } = props;

  const analytics = useAnalytics();
  const timelineData = useTimelineData();

  const availableNow = useMemo(
    () =>
      timelineData.type === 'data'
        ? _(
            timelineData.data.invoicesAndBenefits
              .filter((x) => isInvoicePresent(x))
              .flatMap((x) => x.specialBenefits)
          )
            .uniq()
            .value()
        : [],
    [timelineData]
  );

  const availableSoon = useMemo(
    () =>
      timelineData.type === 'data'
        ? _(
            timelineData.data.invoicesAndBenefits
              .filter((x) => !isInvoicePresent(x))
              .flatMap((x) => x.specialBenefits)
          )
            .uniq()
            .difference(availableNow)
            .value()
        : [],
    [timelineData, availableNow]
  );

  return (
    <ConfirmModal
      title="Don’t miss out on your loyalty benefits"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={true}
      titleIcon={<GiftBox fill={StyleVariables.cyan500} className="d-block" />}
      inlineTitleIcon={true}
      footer={{
        type: 'show',
        applyButton: {
          label: 'Redeem benefits',
          variant: 'primary',
          onClick: () => {
            openUrlInNewTab(billingPortalConfig.loyaltyBenefitsConsultationUrl, {
              analytics,
              analyticsEvent: AnalyticsEvent.CONSULTATION_SCHEDULER_OPENED,
              properties: {
                source: 'cancellation',
                availableNow,
                availableSoon,
              },
            });
            onCancel();
          },
        },
        cancelButton: {
          label: 'Dismiss',
          onClick: onCancel,
        },
        extraButtons: [
          {
            label: 'Cancel anyway',
            variant: 'secondary',
            onClick: onNext,
          },
        ],
      }}
    >
      <>
        <div className="cancellation-intro__description">
          {timelineData.type === 'data'
            ? availableNow.length > 0 && (
                <>
                  <span>Your already available loyalty benefits include:</span>
                  <div
                    className="benefits__special-benefits-container"
                    data-test="benefits__available-special-benefits-container"
                  >
                    <>
                      {availableNow.map((x) => (
                        <SpecialBenefit key={x} benefit={x} isAvailable={true} />
                      ))}
                    </>
                  </div>
                </>
              )
            : timelineData.statusComponent}

          {availableSoon.length > 0 && (
            <>
              <span>These benefits will become available soon:</span>
              <div
                className="benefits__special-benefits-container"
                data-test="benefits__available-soon-special-benefits-container"
              >
                <>
                  {availableSoon.map((x) => (
                    <SpecialBenefit key={x} benefit={x} isAvailable={false} />
                  ))}
                </>
              </div>
            </>
          )}

          <span>
            If you cancel now, you will lose these benefits, as your account will be closed.
            Schedule a consultation before you cancel, and our manager will help you make the most
            of your rewards.
          </span>
        </div>
      </>
    </ConfirmModal>
  );
};
